<template>
  <List
    :total="model.total_count"
    v-model="lm"
    :pagination="pagination"
  >
    <div class="scrollable-table">
      <table
        class="table table-hover"
        :style="{ 'margin-bottom': tableHeight }"
      >
        <thead>
          <tr>
            <ListTh
              :cfg="{ ...config.orders, ...config.medicine_trademarks, ...users?.author }"
              v-for="column in columns"
              :column-id="column.path || column.title"
              :width="column.width"
              :key="column.title"
              @changeTdStyle="changeTdStyle"
            >
              {{ column.label }}
            </ListTh>
            <th>Действия</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in model.data"
            :key="row.id"
          >
            <td
              v-for="(col, name) in columns"
              :key="name"
            >
              <template v-if="col.getter">
                {{ col.getter(row) }}
              </template>
              <template v-else-if="col.path">
                <template v-if="col.date && typeof _result(row, col.path) === 'string'">
                  {{ commons.dateLocal(_result(row, col.path)) }}
                </template>
                <template v-else>
                  {{ _result(row, col.path) }}
                </template>
              </template>
              <template v-else-if="col.date && row[col.title]">
                {{ commons.dateLocal(row[col.title]) }}
              </template>
              <template v-else>
                {{ row[col.title] }}
              </template>
            </td>
            <td
              class="text-center"
              :style="tdStyle"
            >
              <div class="hstack gap-1">
                <template v-if="modalstack">
                  <RouterLink
                    active-class=""
                    :to="{ name: 'orders.item', params: { id: row.id } }"
                    target="_blank"
                    class="btn btn-success btn-sm rounded-circle"
                  >
                    <i class="bi bi-box-arrow-up-right"></i>
                  </RouterLink>
                  <button
                    class="btn btn-success btn-sm rounded-circle"
                    type="button"
                    @click="$emit('modalstack:close', row)"
                  >
                    <i class="bi bi-check"></i>
                  </button>
                </template>
                <RouterLink
                  v-else
                  :to="{ name: 'orders.item', params: { id: row.id } }"
                  title="Редактировать заявку"
                  class="btn btn-success btn-sm rounded-circle"
                >
                  <i class="bi bi-pencil-fill"></i>
                </RouterLink>
                <RouterLink
                  v-if="userStore.can(['orders_price_view', 'orders_price_edit'])"
                  :to="{ name: 'orders.item.cost', params: { id: row.id } }"
                  title="К детализации стоимости тестирования"
                  class="btn btn-warning btn-sm rounded-circle"
                >
                  <i class="bi bi-currency-dollar"></i>
                </RouterLink>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </List>
</template>

<script setup>
import { computed, inject, onMounted, ref, watch } from 'vue'
import { result as _result } from 'lodash-es'
import { storeToRefs } from 'pinia'
import { commons } from '@provise/lib/transformer'
import { useApi } from '@provise/composables/api'
import { useUserStore } from '@provise/stores/user'
import List from '@provise/components/list/list.vue'
import ListTh from '@provise/components/list/th.vue'
import { useConfigsStore } from '@/stores/configs'
import { useActivityStore } from '@/stores/activity'

const { config } = storeToRefs(useConfigsStore())

const emits = defineEmits(['list-query', 'modalstack:close'])
const props = defineProps({
  modalstack: {
    type: Boolean,
    default: false,
  },
  initial: {
    type: Object,
    default: () => ({}),
  },
  pagination: {
    type: Boolean,
    default: true,
  },
})

const model = inject('model')
const userStore = useUserStore()
const activityStore = useActivityStore()
const lm = ref(props.initial)

const tableHeight = computed(() => {
  if (model.value.total_count < 6 && document.location.pathname !== '/') {
    const margin = 6 + (5 - model.value.total_count) * 3 + 'em'
    return margin
  }
  return `auto`
})

const { model: users, refresh: fetchUsers } = useApi({
  url: 'users_list',
  query: { service: 'rtms' },
  data: [],
  pick: d => ({
    data: d.data ?? [],
    author: {
      assign_user: d.data.map(user => ({
        id: user.id,
        value: user.fio ? `${user.fio} (${user.username})` : user.username,
      })),
      'io_assign_user.username': true,
    },
    total_count: d.total_count ?? 0,
  }),
})

onMounted(fetchUsers)

const tdStyle = ref({})
const changeTdStyle = isOpen => {
  if (isOpen) {
    tdStyle.value = { position: 'static', mask: 'unset' }
  } else {
    tdStyle.value = {}
  }
}

const fullscreen = inject('fullscreen', false)
const columns = computed(() => {
  const fs = fullscreen.value
  const cl = [
    { title: 'id', label: '№ заявки', width: '60px' },
    { title: 'name', label: 'Наименование ЛП', path: 'medicine_trademark.name', width: '150px' },
    {
      title: 'medicinal_form',
      label: 'Лекaрственная форма',
      path: 'medicine_trademark.medicinal_form.value',
      width: '200px',
    },
    {
      title: 'activity',
      label: 'Назначение',
      path: 'activity.activity_subtype.parent_name',
      width: '120px',
      getOptions: activityStore.getOptions,
    },
    {
      title: 'activity_subtype',
      label: 'Тип назначения',
      path: 'activity.activity_subtype.name',
      width: '150px',
      getOptions: activityStore.getOptionsSub,
    },
    {
      title: 'date_created',
      label: 'Дата создания',
      path: 'date_created',
      date: true,
      width: '160px',
    },
    {
      title: 'laboratory.name',
      label: 'Лаборатория',
      path: 'laboratory.name',
      width: '150px',
    },
    { title: 'status', label: 'Статус', path: 'status.value', width: '60px' },
    {
      title: 'assign_user',
      label: 'Ответственный',
      path: 'assign_user.username',
      width: '120px',
      getter: o => o.assign_user?.fio || o.assign_user?.username,
    },
    {
      title: 'dispatch_date_expected',
      label: 'Планируемая дата подачи',
      path: 'activity.shipment_date_scheduled',
      date: true,
      width: '160px',
    },
    {
      title: 'dispatch_date_executed',
      label: 'Фактическая дата подачи',
      path: 'dispatch_date_executed',
      date: true,
      width: '160px',
    },
  ]
  return cl.map(c => {
    if (fs) {
      delete c.width
    }
    return c
  })
})

watch(lm, v => emits('list-query', v))
</script>
